import { useEffect, useState } from 'react';
import { Percent, Save, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useModal } from '../contexts/ModalContext';
import {
  getAgencyDefaultCommission,
  setAgencyDefaultCommission,
  isSettingsStoreUnavailable,
} from '../services/settingsService';
import { log } from '../lib/logger';

/**
 * Agency-wide settings (Admin → Definições). Today a single value: the
 * default commission a new campaign starts with when the chosen creator has
 * no default of their own.
 */
export function AgencySettingsPanel() {
  const { t, translateError } = useLanguage();
  const { toast } = useModal();
  const [pctInput, setPctInput] = useState('');
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let active = true;
    getAgencyDefaultCommission().then((pct) => {
      if (!active) return;
      setPctInput(String(Math.round(pct * 10000) / 100));
      setUnavailable(isSettingsStoreUnavailable());
      setLoaded(true);
    });
    return () => { active = false; };
  }, []);

  const handleSave = async () => {
    const pct = parseFloat(pctInput.replace(',', '.'));
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      toast.error(t('error.validationFailed'));
      return;
    }
    setSaving(true);
    try {
      await setAgencyDefaultCommission(pct / 100);
      toast.success(t('admin.settings.saved'));
    } catch (err) {
      log.error('[AgencySettings] Failed to save:', err);
      toast.error(translateError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm ring-1 ring-gray-100 p-6 space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">{t('admin.settings.title')}</h2>
      </div>

      {unavailable && (
        <div className="flex items-start gap-2 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-sm text-amber-800">{t('admin.settings.unavailable')}</p>
        </div>
      )}

      <div className="max-w-md space-y-2">
        <label className="cm-label block text-xs font-medium text-gray-600">
          {t('admin.settings.defaultCommission')}
        </label>
        <div className="flex items-center gap-2">
          <div className="cm-affix has-left relative flex-1">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <Percent className="w-4 h-4" />
            </span>
            <input
              type="number"
              min="0"
              max="100"
              step="0.5"
              value={pctInput}
              onChange={(e) => setPctInput(e.target.value)}
              disabled={!loaded || unavailable}
              className="cm-input"
            />
          </div>
          <button
            type="button"
            onClick={handleSave}
            disabled={!loaded || unavailable || saving}
            className="cm-btn cm-btn-primary inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {t('admin.settings.save')}
          </button>
        </div>
        <p className="text-xs text-gray-500">{t('admin.settings.defaultCommissionHint')}</p>
      </div>
    </div>
  );
}
